import React, { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";

import ContentTabs from "./ContentTabs";
import {
  CREATE_CONTENT,
  STATUSES,
  UPDATE_CONTENT,
} from "../../actions/ActionTypes";
import { notifError, notifSuccess } from "../../actions/FlashMessageActions";
import { postApi } from "../../actions/SendActions";
import EncoderAPI from "../../api/EncoderAPI";
import MediaAPI from "../../api/MediaAPI";
import { EDITOR_ADMIN, OWNER, PROJECT_ADMIN } from "../../constants/roles";
import "../../css/admin/smartlist.scss";
import useAbility from "../../hooks/useAbility";
import { getDefaultProjectLanguage } from "../../utils/data/Content";
import { hasInvalidViewsCount } from "../../utils/mediaRights";
import {
  getFastSchedulingConflictSlotList,
  getMediaApiErrorMessageList,
} from "../../utils/translationAdapters/media";
import {
  checkEIDRCode,
  checkISANCode,
  slugIsValid,
  transformSlug,
} from "../../utils/Translations";
import FastSchedulingConflictDialog from "../contentTabs/rights/FastSchedulingConflictDialog";
import { GET_FAST_MEDIA_LIST_QUERY_KEY } from "../fast_channels/mediaLibrary/MediaLibrary";
import { HOSTED_CONTENTS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type {
  FastSchedulingConflictSlot,
  GetMediaResponse,
} from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";

const mediaListQueryKeyPrefix = MediaAPI.generateQuery({
  fn: MediaAPI.getMediaList,
  params: [
    {
      page: 1,
      perPage: 10,
      filter: {},
      order: { field: "updated_at", direction: "down" },
    },
  ],
}).queryKey.slice(0, 1);

interface LocationState {
  audio_only?: boolean;
}

export default function ContentPage(): JSX.Element | null {
  const [media, setMedia] = useState<GetMediaResponse>();
  const [isSaved, setIsSaved] = useState(true);
  const [fastConflictSlotList, setFastConflictSlotList] = useState<
    FastSchedulingConflictSlot[] | null
  >(null);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation("editor-content");
  const dispatch = useDispatch();
  const { id, type: contentType } = useParams<{
    id: string;
    type: "hosted" | "live" | "master" | "fast" | "external";
  }>();
  const isNew = id === "new";
  const location = useLocation<LocationState | null>();

  const getMediaQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMedia,
    params: [id],
    options: {
      enabled: Boolean(id) && !isNew,
    },
  });

  const { data: mediaData, isLoading } = useQuery(getMediaQuery);

  const { hasRole } = useAbility();

  const invalidateGetMediaQuery = (): void => {
    queryClient.invalidateQueries(getMediaQuery.queryKey);
  };

  const updateMediaMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: GetMediaResponse;
      showMessage?: boolean;
      onSuccess?: () => void;
    }) => MediaAPI.updateMedia({ uuid, data }),
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries(getMediaQuery.queryKey);
      const previousMedia = queryClient.getQueryData<GetMediaResponse>(
        getMediaQuery.queryKey,
      );
      queryClient.setQueryData(getMediaQuery.queryKey, data);
      return { previousMedia };
    },
    onError: (err, _variables, context) => {
      if (context?.previousMedia) {
        queryClient.setQueryData(getMediaQuery.queryKey, context.previousMedia);
      }
      const conflictSlotList = getFastSchedulingConflictSlotList(err);
      if (conflictSlotList) {
        setFastConflictSlotList(conflictSlotList);
        return;
      }
      const errorMessages = getMediaApiErrorMessageList(err);
      if (errorMessages.length) {
        errorMessages.forEach((msg) => {
          dispatch(notifError(msg, []));
        });
      } else {
        dispatch(notifError(t("editor-notif:saveError"), []));
      }
    },
    onSuccess: (_data, variables) => {
      if (variables.showMessage) {
        dispatch(notifSuccess(t("editor-notif:saveSuccess")));
      }
      dispatch({ type: UPDATE_CONTENT, status: STATUSES.SUCCESSFUL });
      setIsSaved(true);
      invalidateGetMediaQuery();
      queryClient.invalidateQueries(mediaListQueryKeyPrefix);
      if (variables.onSuccess) {
        variables.onSuccess();
      }
    },
  });

  const getMediaFastAssetsQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediaFastAssets,
    params: [id],
    options: {
      enabled: Boolean(id) && !isNew,
    },
  });

  const { data: mediaFastAssets } = useQuery(getMediaFastAssetsQuery);

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const {
    language_uuid: languageUuid,
    language_uuid_index: languageUuidIndex,
  } = getDefaultProjectLanguage(languages, project);

  const createContent = useCallback((): void => {
    const audioOnly = location.state?.audio_only;

    if (!contentType) {
      console.error("No content type provided");
      return;
    }

    dispatch(
      postApi({
        endpoint: "/api/media/v6/admin/medias",
        type: CREATE_CONTENT,
        item: "contents",
        token,
        data: {
          begin_date: moment().format(),
          countries: [],
          duration: contentType === "live" ? 3600 : 0,
          enabled: true,
          end_date: null,
          status: "enabled",
          geoblock_mode: "disabled",
          label: t("newContent"),
          smartlist_items: [],
          temporal_restriction: false,
          translations: [
            {
              default: true,
              language_uuid: languageUuid,
              description: "",
              short_description: "",
              name: "",
            },
          ],
          translations_indexes: {
            language_uuid: languageUuidIndex,
            default: { true: [0] },
          },
          type: contentType,
          audio_only: audioOnly,
          genres: [],
          keywords: [],
        },
        callback: ({ uuid }: { uuid: string }) => {
          queryClient.invalidateQueries(mediaListQueryKeyPrefix);
          history.replace(`/contents/${contentType}/${uuid}`);
        },
      }),
    );
  }, [
    dispatch,
    queryClient,
    token,
    t,
    languageUuid,
    languageUuidIndex,
    location.state,
    contentType,
    history,
  ]);

  const createContentRef = useRef(createContent);
  createContentRef.current = createContent;

  const hasCreatedRef = useRef(false);

  useEffect(() => {
    if (isNew && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      createContentRef.current();
    } else {
      if (!isSaved) return;

      setMedia(mediaData);
    }
  }, [isNew, isSaved, mediaData, languages.data, id]);

  const itemIsValid = (item: GetMediaResponse): string[] => {
    const errorMessages = [];

    if (!item.label || item.label.length <= 0) {
      errorMessages.push(t("editor-errors:noLabel"));
    }

    if (
      item.end_date &&
      moment(item.end_date).isBefore(moment(item.begin_date))
    ) {
      errorMessages.push(t("editor-errors:endBeforeStartDate"));
    }

    if (!checkISANCode(item)) {
      errorMessages.push(t("editor-errors:isanCodeError"));
    }

    if (!checkEIDRCode(item)) {
      errorMessages.push(t("editor-errors:eidrCodeError"));
    }

    item.translations?.forEach((translation) => {
      if (!slugIsValid(translation?.slug))
        // eslint-disable-next-line no-param-reassign
        translation.slug = transformSlug(translation.slug);
    });

    if (hasInvalidViewsCount(item)) {
      errorMessages.push(t("editor-errors:viewsCountInvalid"));
    }

    return errorMessages;
  };

  const handleSubmit = (
    item: GetMediaResponse,
    disableValidation?: boolean,
    showMessage?: boolean,
    onSuccess?: () => void,
  ): void => {
    const getStreamsQueryKey = EncoderAPI.generateQuery({
      fn: EncoderAPI.getStreams,
      params: [item.uuid],
    }).queryKey;

    queryClient.invalidateQueries(getStreamsQueryKey);
    queryClient.invalidateQueries([GET_FAST_MEDIA_LIST_QUERY_KEY]);

    const errorMessages = disableValidation ? [] : itemIsValid(item);
    if (errorMessages.length === 0) {
      updateMediaMutation.mutate({
        uuid: item.uuid,
        data: item,
        showMessage,
        onSuccess,
      });
    } else {
      dispatch(notifError(t("editor-notif:saveError"), errorMessages));
    }
  };

  const masterMediaUuid = mediaData?.master_media_uuid;
  if (!isNew && !isLoading && masterMediaUuid && masterMediaUuid !== id) {
    // A child media (version/trailer/bonus) has no page of its own; it's only editable via its master's tabs.
    return <Redirect to={`${HOSTED_CONTENTS_ROUTE}/${masterMediaUuid}`} />;
  }

  if (!media) return null;

  const ownerRole = hasRole([OWNER, EDITOR_ADMIN, PROJECT_ADMIN]);

  const fastAssetsMp4Status = mediaFastAssets?.fast_assets?.mp4_status ?? null;

  return (
    <div className="flex-1 overflow-x-hidden">
      <div className="w-full p-[30px]">
        <ContentTabs
          fastAssetsMp4Status={fastAssetsMp4Status}
          isDisabled={false}
          isLoading={isLoading}
          isNew={isNew}
          isSaved={isSaved}
          isSaving={updateMediaMutation.isLoading}
          item={media}
          onArchive={invalidateGetMediaQuery}
          onSubmit={handleSubmit}
          ownerRole={ownerRole}
          setIsSaved={setIsSaved}
          setItem={setMedia}
        />
      </div>
      {fastConflictSlotList ? (
        <FastSchedulingConflictDialog
          onClose={() => setFastConflictSlotList(null)}
          slotList={fastConflictSlotList}
        />
      ) : null}
    </div>
  );
}
